import { useMemo } from 'react';
import { TrendingUp, Calendar, PoundSterling } from 'lucide-react';
import { useTimesheetForScreen } from '@/contexts/TimesheetContext';
import { useDataSource } from '@/contexts/DataSourceContext';
import { useDashboardSummaryContext } from '@/contexts/DashboardSummaryContext';
import { format, startOfWeek, addDays } from 'date-fns';
import { NavigationMetricCardAlt } from '@/components/NavigationMetricCardAlt';
import type { TimesheetReportFilters } from '@/types/timesheetReport';

type AppNavigationState = {
  reportFilters?: TimesheetReportFilters;
};

interface TimesheetStatsProps {
  onNavigate?: (path: string, state?: AppNavigationState) => void;
}

export function TimesheetStats({ onNavigate }: TimesheetStatsProps) {
  const { timesheets, currentEmployee, projects } = useTimesheetForScreen('homepage');
  const { getScreenMode } = useDataSource();
  const { summary } = useDashboardSummaryContext();
  
  const mode = getScreenMode('homepage');

  const stats = useMemo(() => {
    // Use live data from backend if available and in live mode
    if (mode === 'live' && summary?.performanceMetrics?.billableRevenue && summary?.performanceMetrics?.thisMonth) {
      return {
        currentWeekHours: summary.performanceMetrics.billableRevenue.totalHours,
        billableHours: summary.performanceMetrics.billableRevenue.billableHours,
        billablePercentage: summary.performanceMetrics.billableRevenue.percentage,
        submittedThisMonth: summary.performanceMetrics.thisMonth.submittedCount,
        avgWeeklyHours: summary.performanceMetrics.avgWeeklyHoursLast4Weeks
      };
    }

    // In live mode, show null (renders as "—") until API data arrives
    // This prevents showing incorrect demo/fallback data
    if (mode === 'live') {
      return null;
    }

    // Fall back to demo data calculation (demo mode only)
    const currentWeek = startOfWeek(new Date(), { weekStartsOn: 1 });
    const currentWeekEnd = addDays(currentWeek, 6);

    // Current week timesheet (only include fully approved timesheets)
    const currentTimesheet = timesheets.find(
      ts => ts.employeeId === currentEmployee.id &&
      format(ts.weekEnding, 'yyyy-MM-dd') === format(currentWeekEnd, 'yyyy-MM-dd') &&
      ts.status === 'approved'
    );

    // Calculate current week hours
    const currentWeekHours = currentTimesheet?.entries.reduce((sum, entry) => {
      return sum + Object.values(entry.hours).reduce((h, v) => h + v, 0);
    }, 0) || 0;

    // Calculate billable percentage
    const billableHours = currentTimesheet?.entries.reduce((sum, entry) => {
      const project = projects.find(p => p.id === entry.projectId);
      if (project?.billable) {
        return sum + Object.values(entry.hours).reduce((h, v) => h + v, 0);
      }
      return sum;
    }, 0) || 0;

    const billablePercentage = currentWeekHours > 0 
      ? Math.round((billableHours / currentWeekHours) * 100) 
      : 0;

    // Count submitted timesheets this month
    const thisMonth = new Date().getMonth();
    const submittedThisMonth = timesheets.filter(
      ts => ts.employeeId === currentEmployee.id &&
      ts.status !== 'draft' &&
      ts.weekEnding.getMonth() === thisMonth &&
      ts.entries.length > 0
    ).length;

    // Calculate average weekly hours (last 4 weeks)
    const last4Weeks = timesheets
      .filter(ts => ts.employeeId === currentEmployee.id && ts.status !== 'draft')
      .slice(0, 4);

    const avgWeeklyHours = last4Weeks.length > 0
      ? last4Weeks.reduce((sum, ts) => {
          return sum + ts.entries.reduce((entrySum, entry) => {
            return entrySum + Object.values(entry.hours).reduce((h, v) => h + v, 0);
          }, 0);
        }, 0) / 4
      : 0;

    return {
      currentWeekHours,
      billableHours,
      billablePercentage,
      submittedThisMonth,
      avgWeeklyHours
    };
  }, [mode, summary, timesheets, currentEmployee, projects]);

  // Determine performance levels for variant mapping
  const billableVariant = !stats ? 'info' :
                          stats.billablePercentage >= 80 ? 'success' : 
                          stats.billablePercentage >= 75 ? 'info' : 
                          stats.billablePercentage >= 60 ? 'warning' : 'error';
  
  const monthlyVariant = !stats ? 'info' :
                         stats.submittedThisMonth >= 4 ? 'success' :
                         stats.submittedThisMonth >= 3 ? 'info' : 'warning';
  
  // Avg Weekly is informational only, always use info variant
  const avgWeeklyVariant = 'info';

  // Generate change labels based on performance
  const billableChangeLabel = !stats ? 'Loading...' :
                              stats.billablePercentage >= 75 ? 'Above target (75%)' :
                              stats.billablePercentage === 75 ? 'On target (75%)' :
                              'Below target (75%)';

  return (
    <>
      {/* Billable Revenue Card */}
      <NavigationMetricCardAlt
        title="Billable Revenue (This Week)"
        value={stats ? `${stats.billablePercentage}%` : '—'}
        icon={PoundSterling}
        variant={billableVariant}
        changeLabel={billableChangeLabel}
        onClick={() => {
          const currentWeek = startOfWeek(new Date(), { weekStartsOn: 1 });
          const currentWeekEnd = addDays(currentWeek, 6);
          currentWeekEnd.setHours(23, 59, 59, 999);
          onNavigate?.('/reports/timesheet', {
            reportFilters: {
              dateFrom: currentWeek,
              dateTo: currentWeekEnd,
              quickDateRange: 'this-week',
              employeeIds: [currentEmployee.id],
            },
          });
        }}
        ariaLabel="View billable revenue report"
      />

      {/* This Month Card */}
      <NavigationMetricCardAlt
        title="This Month"
        value={stats ? stats.submittedThisMonth.toString() : '—'}
        icon={Calendar}
        variant={monthlyVariant}
        changeLabel={stats ? 'Timesheets submitted' : 'Loading...'}
        onClick={() => {
          const now = new Date();
          const firstDay = new Date(now.getFullYear(), now.getMonth(), 1);
          const lastDay = new Date(now.getFullYear(), now.getMonth() + 1, 0);
          lastDay.setHours(23, 59, 59, 999);
          onNavigate?.('/reports/timesheet', {
            reportFilters: {
              dateFrom: firstDay,
              dateTo: lastDay,
              quickDateRange: 'this-month',
              employeeIds: [currentEmployee.id],
            },
          });
        }}
        ariaLabel="View monthly timesheet report"
      />

      {/* Avg Weekly Card */}
      <NavigationMetricCardAlt
        title="Avg Weekly"
        value={stats ? `${stats.avgWeeklyHours.toFixed(1)}h` : '—'}
        icon={TrendingUp}
        variant={avgWeeklyVariant}
        changeLabel={stats ? 'Last 4 weeks average' : 'Loading...'}
        onClick={() => onNavigate?.('/reports/utilisation', {
          reportFilters: {
            employeeIds: [currentEmployee.id],
          },
        })}
        ariaLabel="View utilisation report"
      />
    </>
  );
}
